const API_ORIGIN = import.meta.env.VITE_API_URL?.replace(/\/+$/, '')
const BASE_URL = API_ORIGIN ? `${API_ORIGIN}/api` : '/api'

/** Resolve an API-owned public asset path for local development and production. */
export function getPublicAssetUrl(path?: string | null): string {
  if (!path) return ''
  if (/^(?:https?:|data:|blob:)/i.test(path)) return path
  const normalized = path.startsWith('/') ? path : `/${path}`
  return API_ORIGIN ? `${API_ORIGIN}${normalized}` : normalized
}

export class ApiError extends Error {
  code: string
  override message: string

  constructor(code: string, message?: string) {
    super(code)
    this.code = code
    this.message = message ?? code
  }
}

/**
 * POST /api/{endpoint} — the single API surface.
 * Returns the JSONB payload from router_api().
 */
export async function api<T = unknown>(
  endpoint: number,
  payload: Record<string, unknown> = {},
  token?: string,
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(`${BASE_URL}/${endpoint}`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  })

  const data = await res.json()

  // router_api errors live inside the JSON envelope, not HTTP status codes.
  // Exception: 405, 400, 413 are set by the Go backend.
  if (res.status >= 400) {
    throw new ApiError(data.error ?? 'http_error', data.message)
  }

  if (data && typeof data === 'object' && 'error' in data) {
    throw new ApiError(data.error, data.message)
  }

  return data as T
}

export interface ImageUploadResponse {
  image: import('./types').Imgx
  original: string
  thumb: string
  large: string
}

/** Upload a product image and create its image-library row in one request. */
export async function uploadImage(
  file: File,
  options: { alt_text?: string; title?: string } = {},
  token?: string,
): Promise<ImageUploadResponse> {
  const authToken = token ?? getAuthToken()
  if (!authToken) throw new ApiError('unauthorized')

  const form = new FormData()
  form.append('image', file)
  if (options.alt_text) form.append('alt_text', options.alt_text)
  if (options.title) form.append('title', options.title)

  const res = await fetch(`${BASE_URL}/upload/image`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${authToken}` },
    body: form,
  })
  const data = await res.json()
  if (!res.ok) throw new ApiError(data.error ?? 'upload_failed', data.message)
  return data as ImageUploadResponse
}

// ── Endpoint constants ────────────────────────────────

export const EP = {
  // Auth (public)
  SYX_LOGIN: 100,
  USERX_REGISTER: 101,
  USERX_LOGIN: 105,
  PASSWORD_RESET_REQUEST: 106,
  PASSWORD_RESET_CONFIRM: 107,
  EMAIL_VERIFY: 108,
  EMAIL_VERIFY_RESEND: 109,
  USERX_REGISTER_CODE_REQUEST: 110,
  STORE_SETTINGS_PUBLIC: 10014,

  // Auth (authenticated)
  LOGOUT: 102,
  CHANGE_PASSWORD: 103,

  // Products (public)
  PRODUCT_LIST: 10001,
  PRODUCT_DETAIL: 10002,
  CATEGORIES: 10003,
  FEATURED_PRODUCTS: 10004,

  // Cart
  CART_GET: 20001,
  CART_ADD: 20002,
  CART_UPDATE: 20003,
  CART_REMOVE: 20004,
  CART_CLEAR: 20005,

  // Orders
  ORDER_CREATE: 30001,
  ORDER_GET: 30002,
  ORDER_LIST: 30003,
  ORDER_CANCEL: 30004,
  CUSTOMER_WALLET: 30005,

  // External products
  EXTERNAL_SOURCES: 50001,
  EXTERNAL_PRODUCT_GET: 50002,
  EXTERNAL_PRODUCT_SEARCH: 50003,

  // Admin — customers
  ADMIN_CUSTOMER_LIST: 90001,
  ADMIN_CUSTOMER_DETAIL: 90002,
  ADMIN_CUSTOMER_UPDATE: 90003,
  ADMIN_CUSTOMER_DELETE: 90004,
  ADMIN_CREDIT_TRANSACTION_CREATE: 90005,
  ADMIN_CREDIT_TRANSACTION_LIST: 90006,

  // Admin — syx users (Phase 1)
  ADMIN_SYX_LIST: 90010,           // List users (Root and Manager)
  ADMIN_SYX_DETAIL: 90011,         // Get user (Root and Manager)
  ADMIN_SYX_CREATE: 90012,         // Create user (Root only)
  ADMIN_SYX_CHANGE_ROLE: 90013,    // Change role (Root only)
  ADMIN_SYX_RESET_PASSWORD: 90014, // Reset password (Root and Manager)

  // Admin — products
  ADMIN_PRODUCT_CREATE: 90020,
  ADMIN_PRODUCT_UPDATE: 90021,
  ADMIN_PRODUCT_DELETE: 90022,

  // Admin — orders
  ADMIN_ORDER_LIST: 90030,
  ADMIN_ORDER_UPDATE: 90031,
  ADMIN_ORDER_GET: 90032,

  // Admin — external
  ADMIN_REFRESH_EXTERNAL: 90040,

  // Admin — mail configuration (Phase 1)
  MAIL_CONFIG_GET: 90060,          // Get mail config (Root only)
  MAIL_CONFIG_UPDATE: 90061,       // Update mail config (Root only)

  // Admin — global store configuration
  STORE_SETTINGS_GET: 90070,
  STORE_SETTINGS_UPDATE: 90071,

  // Catalog — global items
  ITEM_LIST: 10010,
  ITEM_DETAIL: 10011,
  ITEM_TREE: 10012,
  STOCK_AVAILABLE: 10013,

  // Admin — items (Root only)
  ADMIN_ITEM_CREATE: 90080,
  ADMIN_ITEM_UPDATE: 90081,
  ADMIN_ITEM_DELETE: 90082,

  // Admin — stock (Root only)
  ADMIN_STOCK_CREATE: 90083,
  ADMIN_STOCK_UPDATE: 90084,
  ADMIN_STOCK_SET_STATUS: 90085,
  ADMIN_STOCK_LIST: 90086,
  ADMIN_STOCK_GET: 90087,
  ADMIN_STOCK_BULK_CREATE: 90088,

  // Admin — images (Root only)
  ADMIN_IMAGE_CREATE: 90090,
  ADMIN_IMAGE_UPDATE: 90091,
  ADMIN_IMAGE_DELETE: 90092,
  ADMIN_IMAGE_LIST: 90093,
  ADMIN_IMAGE_GET: 90094,

  // Backend fetch engine
  FETCH_EXTERNAL: 99999,
} as const

// ── Typed API Methods ─────────────────────────────────

import type {
  AuthCodeRequestResponse,
  SyxLoginResponse,
  UserxRegisterResponse,
  UserxLoginResponse,
  UserxWalletResponse,
  ProductListResponse,
  Product,
  CategoryListResponse,
  ProductCreateResponse,
  CartResponse,
  OrderCreateResponse,
  Order,
  OrderListResponse,
  ExternalSourceListResponse,
  ExternalProduct,
  ExternalProductSearchResponse,
  UserxUserListResponse,
  UserxUser,
  CreditTransactionCreateRequest,
  CreditTransactionCreateResponse,
  CreditTransactionListResponse,
  SyxUserListResponse,
  SyxUser,
  AdminOrderListResponse,
  StoreSettings,
} from './types'

/**
 * Get auth token from the auth module if available
 */
function getAuthToken(): string | undefined {
  if (typeof localStorage === 'undefined') return undefined
  return localStorage.getItem('dpweb_token') ?? undefined
}

// ── Auth Methods ──────────────────────────────────────

export async function syxLogin(email: string, password: string): Promise<SyxLoginResponse> {
  return api<SyxLoginResponse>(EP.SYX_LOGIN, { email, password })
}

export async function userxRegister(
  email: string,
  password: string,
  code?: string,
): Promise<UserxRegisterResponse> {
  return api<UserxRegisterResponse>(EP.USERX_REGISTER, { email, password, ...(code ? { code } : {}) })
}

export async function requestUserxRegistrationCode(
  email: string,
): Promise<AuthCodeRequestResponse> {
  return api<AuthCodeRequestResponse>(EP.USERX_REGISTER_CODE_REQUEST, { email })
}

export async function userxLogin(
  email: string,
  password: string,
): Promise<UserxLoginResponse> {
  return api<UserxLoginResponse>(EP.USERX_LOGIN, { email, password })
}

export async function logout(token?: string): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(EP.LOGOUT, {}, token ?? getAuthToken())
}

export async function changePassword(
  old_password: string,
  new_password: string,
  token?: string,
): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(
    EP.CHANGE_PASSWORD,
    { old_password, new_password },
    token ?? getAuthToken(),
  )
}

export async function requestPasswordReset(
  email: string,
  userType: 'userx' | 'syx' = 'userx',
): Promise<{ success: boolean; message: string; retry_after_seconds: number }> {
  return api<{ success: boolean; message: string; retry_after_seconds: number }>(EP.PASSWORD_RESET_REQUEST, {
    email,
    user_type: userType,
  })
}

export async function confirmPasswordReset(
  token: string,
  newPassword: string,
): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(EP.PASSWORD_RESET_CONFIRM, {
    token,
    new_password: newPassword,
  })
}

export async function confirmPasswordResetCode(
  email: string,
  code: string,
  newPassword: string,
  userType: 'userx' | 'syx' = 'userx',
): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(EP.PASSWORD_RESET_CONFIRM, {
    email,
    code,
    new_password: newPassword,
    user_type: userType,
  })
}

export async function verifyEmail(token: string): Promise<{ success: boolean; email: string }> {
  return api<{ success: boolean; email: string }>(EP.EMAIL_VERIFY, { token })
}

export async function verifyEmailCode(email: string, code: string): Promise<{ success: boolean; email: string }> {
  return api<{ success: boolean; email: string }>(EP.EMAIL_VERIFY, { email, code })
}

export async function resendEmailVerification(
  email: string,
): Promise<{ success: boolean; message: string; retry_after_seconds: number }> {
  return api<{ success: boolean; message: string; retry_after_seconds: number }>(EP.EMAIL_VERIFY_RESEND, { email })
}

// ── Product Methods ───────────────────────────────────

export interface ProductListParams extends Record<string, unknown> {
  category?: string
  search?: string
  min_price?: number
  max_price?: number
  sort?: string
  page?: number
  limit?: number
}

export async function listProducts(params?: ProductListParams): Promise<ProductListResponse> {
  return api<ProductListResponse>(EP.PRODUCT_LIST, params ?? {})
}

export async function getProduct(id?: number, slug?: string): Promise<Product> {
  return api<Product>(EP.PRODUCT_DETAIL, { id, slug })
}

export async function getCategories(): Promise<CategoryListResponse> {
  return api<CategoryListResponse>(EP.CATEGORIES, {})
}

export async function getFeaturedProducts(limit?: number): Promise<{ products: Product[] }> {
  return api<{ products: Product[] }>(EP.FEATURED_PRODUCTS, limit ? { limit } : {})
}

// ── Cart Methods ──────────────────────────────────────

export async function getCart(token?: string): Promise<CartResponse> {
  return api<CartResponse>(EP.CART_GET, {}, token ?? getAuthToken())
}

export async function addToCart(
  itemx_i: number,
  quantity?: number,
  token?: string,
): Promise<CartResponse> {
  return api<CartResponse>(EP.CART_ADD, { itemx_i, quantity }, token ?? getAuthToken())
}

export async function updateCart(
  cart_id: number,
  quantity: number,
  token?: string,
): Promise<CartResponse> {
  return api<CartResponse>(EP.CART_UPDATE, { cart_id, quantity }, token ?? getAuthToken())
}

export async function removeFromCart(cart_id: number, token?: string): Promise<CartResponse> {
  return api<CartResponse>(EP.CART_REMOVE, { cart_id }, token ?? getAuthToken())
}

export async function clearCart(token?: string): Promise<CartResponse> {
  return api<CartResponse>(EP.CART_CLEAR, {}, token ?? getAuthToken())
}

// ── Order Methods ─────────────────────────────────────

export interface CreateOrderParams extends Record<string, unknown> {
  items: Array<{ itemx_i: number; quantity: number }>
  notes?: string
  checkout_key?: string
  expected_total: number
}

export async function createOrder(
  params: CreateOrderParams,
  token?: string,
): Promise<OrderCreateResponse> {
  return api<OrderCreateResponse>(EP.ORDER_CREATE, params, token ?? getAuthToken())
}

export async function getOrder(
  order_id?: number,
  order_number?: string,
  token?: string,
): Promise<Order> {
  return api<Order>(EP.ORDER_GET, { order_id, order_number }, token ?? getAuthToken())
}

export interface ListOrdersParams extends Record<string, unknown> {
  status?: string
  page?: number
  limit?: number
}

export async function listOrders(
  params?: ListOrdersParams,
  token?: string,
): Promise<OrderListResponse> {
  return api<OrderListResponse>(EP.ORDER_LIST, params ?? {}, token ?? getAuthToken())
}

export async function cancelOrder(
  order_id: number,
  token?: string,
): Promise<{ success: boolean; order_number: string; status: string }> {
  return api<{ success: boolean; order_number: string; status: string }>(
    EP.ORDER_CANCEL,
    { order_id },
    token ?? getAuthToken(),
  )
}

export async function getCustomerWallet(token?: string): Promise<UserxWalletResponse> {
  return api<UserxWalletResponse>(EP.CUSTOMER_WALLET, {}, token ?? getAuthToken())
}

// ── External Product Methods ──────────────────────────

export async function listExternalSources(): Promise<ExternalSourceListResponse> {
  return api<ExternalSourceListResponse>(EP.EXTERNAL_SOURCES, {})
}

export async function getExternalProduct(
  source_id: number,
  external_id: string,
): Promise<ExternalProduct> {
  return api<ExternalProduct>(EP.EXTERNAL_PRODUCT_GET, { source_id, external_id })
}

export interface SearchExternalParams extends Record<string, unknown> {
  source_id: number
  search?: string
  page?: number
  limit?: number
}

export async function searchExternal(
  params: SearchExternalParams,
): Promise<ExternalProductSearchResponse> {
  return api<ExternalProductSearchResponse>(EP.EXTERNAL_PRODUCT_SEARCH, params)
}

// ── Admin Methods: Customers ──────────────────────────

export interface AdminListParams extends Record<string, unknown> {
  search?: string
  page?: number
  limit?: number
}

export async function adminListCustomers(
  params?: AdminListParams,
  token?: string,
): Promise<UserxUserListResponse> {
  return api<UserxUserListResponse>(EP.ADMIN_CUSTOMER_LIST, params ?? {}, token ?? getAuthToken())
}

export async function adminGetCustomer(userx_id: number, token?: string): Promise<UserxUser> {
  return api<UserxUser>(EP.ADMIN_CUSTOMER_DETAIL, { userx_id }, token ?? getAuthToken())
}

export interface AdminUpdateCustomerParams {
  userx_id: number
  user_level?: number
  status?: number
  display_name?: string
  [key: string]: unknown
}

export async function adminUpdateCustomer(
  params: AdminUpdateCustomerParams,
  token?: string,
): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(EP.ADMIN_CUSTOMER_UPDATE, params, token ?? getAuthToken())
}

export async function adminDeleteCustomer(
  userx_id: number,
  token?: string,
): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(
    EP.ADMIN_CUSTOMER_DELETE,
    { userx_id },
    token ?? getAuthToken(),
  )
}

export async function adminCreateCreditTransaction(
  params: CreditTransactionCreateRequest,
  token?: string,
): Promise<CreditTransactionCreateResponse> {
  return api<CreditTransactionCreateResponse>(
    EP.ADMIN_CREDIT_TRANSACTION_CREATE,
    params as unknown as Record<string, unknown>,
    token ?? getAuthToken(),
  )
}

export async function adminListCreditTransactions(
  params: { userx_i: number; page?: number; limit?: number },
  token?: string,
): Promise<CreditTransactionListResponse> {
  return api<CreditTransactionListResponse>(
    EP.ADMIN_CREDIT_TRANSACTION_LIST,
    params,
    token ?? getAuthToken(),
  )
}

// ── Admin Methods: Syx Users (Phase 1) ───────────────────────────

export interface AdminSyxListParams extends Record<string, unknown> {
  search?: string
  role_min?: number
  role_max?: number
  page?: number
  limit?: number
}

export async function adminListSyx(
  params?: AdminSyxListParams,
  token?: string,
): Promise<SyxUserListResponse> {
  return api<SyxUserListResponse>(EP.ADMIN_SYX_LIST, params ?? {}, token ?? getAuthToken())
}

export async function adminGetSyx(syx_id: number, token?: string): Promise<SyxUser> {
  return api<SyxUser>(EP.ADMIN_SYX_DETAIL, { i: syx_id }, token ?? getAuthToken())
}

export async function adminCreateSyx(
  params: { email: string; password: string; role?: number; display_name?: string },
  token?: string,
): Promise<{ i: number; email: string }> {
  return api<{ i: number; email: string }>(EP.ADMIN_SYX_CREATE, params, token ?? getAuthToken())
}

export async function adminChangeRole(
  syx_id: number,
  role: number,
  token?: string,
): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(
    EP.ADMIN_SYX_CHANGE_ROLE,
    { i: syx_id, role },
    token ?? getAuthToken(),
  )
}

export async function adminResetPassword(
  syx_id: number,
  password: string,
  token?: string,
): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(
    EP.ADMIN_SYX_RESET_PASSWORD,
    { i: syx_id, password },
    token ?? getAuthToken(),
  )
}

// ── Admin Methods: Products ───────────────────────────

export interface AdminCreateProductParams {
  sku: string
  name: string
  slug: string
  price: number
  category: string
  [key: string]: unknown
}

export async function adminCreateProduct(
  params: AdminCreateProductParams,
  token?: string,
): Promise<ProductCreateResponse> {
  return api<ProductCreateResponse>(EP.ADMIN_PRODUCT_CREATE, params, token ?? getAuthToken())
}

export interface AdminUpdateProductParams {
  product_id: number
  name?: string
  price?: number
  [key: string]: unknown
}

export async function adminUpdateProduct(
  params: AdminUpdateProductParams,
  token?: string,
): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(EP.ADMIN_PRODUCT_UPDATE, params, token ?? getAuthToken())
}

export async function adminDeleteProduct(
  product_id: number,
  token?: string,
): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(
    EP.ADMIN_PRODUCT_DELETE,
    { product_id },
    token ?? getAuthToken(),
  )
}

// ── Admin Methods: Orders ─────────────────────────────

export async function adminListOrders(
  params?: ListOrdersParams,
  token?: string,
): Promise<AdminOrderListResponse> {
  return api<AdminOrderListResponse>(EP.ADMIN_ORDER_LIST, params ?? {}, token ?? getAuthToken())
}

export async function adminUpdateOrderStatus(
  order_id: number,
  status: string,
  send_notification = true,
  token?: string,
): Promise<{ success: boolean; status: string }> {
  return api<{ success: boolean; status: string }>(
    EP.ADMIN_ORDER_UPDATE,
    { order_id, status, send_notification },
    token ?? getAuthToken(),
  )
}

// ── Admin Methods: External Products ──────────────────

export async function adminRefreshExternal(
  source_id: number,
  external_ids?: string[],
  token?: string,
): Promise<{ success: boolean; source_id: number }> {
  return api<{ success: boolean; source_id: number }>(
    EP.ADMIN_REFRESH_EXTERNAL,
    { source_id, external_ids },
    token ?? getAuthToken(),
  )
}

export interface FetchExternalParams extends Record<string, unknown> {
  source_id: number
  search?: string
  page?: number
  limit?: number
}

export async function fetchExternal(
  params: FetchExternalParams,
  token?: string,
): Promise<{ source_id: number; count: number; products: ExternalProduct[] }> {
  return api<{ source_id: number; count: number; products: ExternalProduct[] }>(
    EP.FETCH_EXTERNAL,
    params,
    token ?? getAuthToken(),
  )
}

// ── Admin Methods: Global Mail Configuration ────────────────────

export interface MailProfile {
  smtp_host?: string
  smtp_port?: number
  smtp_username?: string
  smtp_password_configured?: boolean
  smtp_password?: string
  smtp_use_tls?: boolean
  from_email?: string
  from_name?: string
  event_log_email?: string
}

export interface MailConfig {
  account: MailProfile
  delivery: MailProfile
}

export async function adminGetMailConfig(token?: string): Promise<MailConfig> {
  return api<MailConfig>(EP.MAIL_CONFIG_GET, {}, token ?? getAuthToken())
}

export interface AdminUpdateMailConfigParams {
  account?: MailProfile
  delivery?: MailProfile
}

export async function adminUpdateMailConfig(
  params: AdminUpdateMailConfigParams,
  token?: string,
): Promise<{ success: boolean; retried_count?: number }> {
  return api<{ success: boolean; retried_count?: number }>(EP.MAIL_CONFIG_UPDATE, params as unknown as Record<string, unknown>, token ?? getAuthToken())
}

export async function testMailAccount(
  purpose: 'account' | 'delivery',
  token?: string,
): Promise<{ success: boolean; message: string }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) headers.Authorization = `Bearer ${token}`
  const response = await fetch(`${BASE_URL}/mail/test`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ purpose }),
  })
  const data = await response.json()
  if (!response.ok) throw new ApiError(data.error ?? 'smtp_test_failed', data.message)
  return data as { success: boolean; message: string }
}

// ── Store Settings ──────────────────────────────────────────────

export async function getPublicStoreSettings(): Promise<StoreSettings> {
  return api<StoreSettings>(EP.STORE_SETTINGS_PUBLIC, {})
}

export async function adminGetStoreSettings(token?: string): Promise<StoreSettings> {
  return api<StoreSettings>(EP.STORE_SETTINGS_GET, {}, token ?? getAuthToken())
}

export interface AdminUpdateStoreSettingsParams {
  name?: string
  description?: string
  logo_url?: string
  domain?: string
  store_url?: string
  contact_email?: string
  contact_phone?: string
  contact_telegram?: string
  timezone?: string
}

export async function adminUpdateStoreSettings(
  params: AdminUpdateStoreSettingsParams,
  token?: string,
): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(EP.STORE_SETTINGS_UPDATE, params as unknown as Record<string, unknown>, token ?? getAuthToken())
}

// ── Catalog Methods (public, global) ───────────────────────────

import type {
  ItemListRequest,
  ItemListResponse,
  ItemDetailRequest,
  ItemDetailResponse,
  ItemTreeRequest,
  ItemTreeResponse,
  StockAvailableRequest,
  StockAvailableResponse,
  AdminItemCreateRequest,
  AdminItemCreateResponse,
  AdminItemUpdateRequest,
  AdminStockCreateRequest,
  AdminStockCreateResponse,
  AdminStockUpdateRequest,
  AdminStockSetStatusResponse,
  AdminStockListRequest,
  AdminStockListResponse,
  AdminStockGetResponse,
  StockStatus,
} from './types'

export async function listItems(params: ItemListRequest): Promise<ItemListResponse> {
  return api<ItemListResponse>(EP.ITEM_LIST, params as unknown as Record<string, unknown>)
}

export async function getItem(params: ItemDetailRequest): Promise<ItemDetailResponse> {
  return api<ItemDetailResponse>(EP.ITEM_DETAIL, params as unknown as Record<string, unknown>)
}

export async function getItemTree(params: ItemTreeRequest): Promise<ItemTreeResponse> {
  return api<ItemTreeResponse>(EP.ITEM_TREE, params as unknown as Record<string, unknown>)
}

export async function getStockAvailable(
  params: StockAvailableRequest,
): Promise<StockAvailableResponse> {
  return api<StockAvailableResponse>(
    EP.STOCK_AVAILABLE,
    params as unknown as Record<string, unknown>,
  )
}

// ── Phase 2: Admin Item/Stock Methods (Root only) ────────────────

export async function adminCreateItem(
  params: AdminItemCreateRequest,
  token?: string,
): Promise<AdminItemCreateResponse> {
  return api<AdminItemCreateResponse>(
    EP.ADMIN_ITEM_CREATE,
    params as unknown as Record<string, unknown>,
    token ?? getAuthToken(),
  )
}

export async function adminUpdateItem(
  params: AdminItemUpdateRequest,
  token?: string,
): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(
    EP.ADMIN_ITEM_UPDATE,
    params as unknown as Record<string, unknown>,
    token ?? getAuthToken(),
  )
}

export async function adminDeleteItem(
  itemId: number,
  token?: string,
): Promise<{ success: boolean; deleted?: number }> {
  return api<{ success: boolean; deleted?: number }>(
    EP.ADMIN_ITEM_DELETE,
    { i: itemId },
    token ?? getAuthToken(),
  )
}

export async function adminCreateStock(
  params: AdminStockCreateRequest,
  token?: string,
): Promise<AdminStockCreateResponse> {
  return api<AdminStockCreateResponse>(
    EP.ADMIN_STOCK_CREATE,
    params as unknown as Record<string, unknown>,
    token ?? getAuthToken(),
  )
}

export async function adminBulkCreateStock(
  itemx_i: number,
  units: Array<{
    srno: string
    code: string
    purchase_price?: number
    purchase_currency?: string
    purchase_exchange_rate?: number
    sale_price: number
    sale_currency?: string
    sale_exchange_rate?: number
  }>,
  token?: string,
): Promise<{ success: boolean; inserted: number }> {
  return api(EP.ADMIN_STOCK_BULK_CREATE, { itemx_i, units }, token ?? getAuthToken())
}

export async function adminUpdateStock(
  params: AdminStockUpdateRequest,
  token?: string,
): Promise<{ success: boolean }> {
  return api<{ success: boolean }>(
    EP.ADMIN_STOCK_UPDATE,
    params as unknown as Record<string, unknown>,
    token ?? getAuthToken(),
  )
}

export async function adminSetStockStatus(
  unitId: number,
  s: StockStatus,
  token?: string,
): Promise<AdminStockSetStatusResponse> {
  return api<AdminStockSetStatusResponse>(
    EP.ADMIN_STOCK_SET_STATUS,
    { i: unitId, s },
    token ?? getAuthToken(),
  )
}

export async function adminListStock(
  params: AdminStockListRequest,
  token?: string,
): Promise<AdminStockListResponse> {
  return api<AdminStockListResponse>(
    EP.ADMIN_STOCK_LIST,
    params as unknown as Record<string, unknown>,
    token ?? getAuthToken(),
  )
}

export async function adminGetStock(
  unitId: number,
  token?: string,
): Promise<AdminStockGetResponse> {
  return api<AdminStockGetResponse>(
    EP.ADMIN_STOCK_GET,
    { i: unitId },
    token ?? getAuthToken(),
  )
}
